import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, Suspense } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, Users, CheckSquare, TrendingUp, LogOut, ShieldCheck, Compass, Crown, LifeBuoy, Settings as SettingsIcon, Volume2, VolumeX, UserPlus, Briefcase, Activity, HeartPulse, BarChart3, MonitorPlay, Loader2, Tv, Merge, AlertTriangle, Repeat, Undo2, Coins, UsersRound, Lightbulb, UserCheck, ScrollText, Rows3, Rows4, DoorOpen, Gauge, CalendarDays, Sparkles, Workflow, Banknote, Kanban } from "lucide-react";
import NotificationCenter from "@/components/NotificationCenter";
import CommApprovalPopup from "@/components/events/CommApprovalPopup";
import SavedViews from "@/components/SavedViews";
import { isSoundEnabled, setSoundEnabled } from "@/lib/sound";
import TaskNotifications from "@/components/TaskNotifications";
import TaskCelebrationOverlay from "@/components/tasks/TaskCelebrationOverlay";
import { GoogleTokenBanner } from "@/components/GoogleTokenBanner";
import { GlobalSearch } from "@/components/GlobalSearch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initials } from "@/lib/helpers";

const STATUS_DOT = { alle: "bg-blue-500", whatsapp: "bg-emerald-400", email: "bg-sky-400", circle: "bg-violet-400", nieuw: "bg-amber-500", open: "bg-blue-500", wacht_op_klant: "bg-orange-500", opgelost: "bg-emerald-500" };

const SECTIONS = [
  { label: "Dashboard", items: [
    { to: "/dashboard", label: "Mijn dag", icon: LayoutDashboard, testid: "nav-dashboard", end: true, section: "mijn_dag" },
    { to: "/dashboard/team-dag", label: "Team dag", icon: UsersRound, testid: "nav-dashboard-team", section: "team_dashboards" },
    { to: "/dashboard/sales", label: "Sales", icon: TrendingUp, testid: "nav-dashboard-sales", section: "team_dashboards" },
    { to: "/dashboard/support", label: "Support", icon: LifeBuoy, testid: "nav-dashboard-support", section: "team_dashboards" },
    { to: "/dashboard/csm", label: "CSM", icon: HeartPulse, testid: "nav-dashboard-csm", section: "team_dashboards" },
    { to: "/tasks", label: "Taken", icon: CheckSquare, testid: "nav-tasks", section: "mijn_dag" },
  ] },
  { label: "Cockpit", items: [
    { to: "/cockpit", label: "EOD Cockpit", icon: Gauge, testid: "nav-cockpit" },
  ] },
  { label: "Sales", items: [
    { to: "/sales/funnel", label: "Salesfunnel", icon: TrendingUp, testid: "nav-salesfunnel", section: "sales" },
    { to: "/sales/statistieken", label: "Statistieken", icon: BarChart3, testid: "nav-sales-stats", section: "sales" },
    { to: "/sales/leads", label: "Leads", icon: UserPlus, testid: "nav-leads", section: "sales" },
    { to: "/sales/deals", label: "Deals", icon: Briefcase, testid: "nav-deals", section: "sales" },
    { to: "/sales/webinars", label: "Webinar-aanmeldingen", icon: MonitorPlay, testid: "nav-webinar-registrations", section: "sales" },
  ] },
  { label: "Klanten", items: [
    { to: "/customers", label: "Overzicht", icon: Users, testid: "nav-customers-overview", end: true, section: "klanten" },
    { to: "/customers/progress", label: "Voortgang", icon: Activity, testid: "nav-customers-progress", section: "klanten" },
    { to: "/customers/reactivation", label: "Heractivatie", icon: HeartPulse, testid: "nav-customers-reactivation", section: "klanten" },
    { to: "/duplicaten", label: "Opruimen", icon: Merge, testid: "nav-duplicates", section: "klanten" },
  ] },
  { label: "Support", support: true, items: [
    { to: "/support/inbox", label: "Alle kanalen", key: "alle", kanaal: "", testid: "nav-support-alle", section: "support" },
    { to: "/support/open?kanaal=whatsapp", label: "WhatsApp", key: "whatsapp", kanaal: "whatsapp", testid: "nav-support-whatsapp", section: "support" },
    { to: "/support/inbox?kanaal=email", label: "E-mail", key: "email", kanaal: "email", testid: "nav-support-email", section: "support" },
    { to: "/support/inbox?kanaal=circle", label: "Circle", key: "circle", kanaal: "circle", testid: "nav-support-circle", section: "support" },
  ] },
  { label: "Schatkaart", items: [
    { to: "/schatkaart", label: "Schatkaart", icon: Compass, testid: "nav-schatkaart", section: "sales" },
  ] },
  { label: "Backoffice", items: [
    { to: "/backoffice/nieuwe-klanten", label: "Nieuwe klanten", icon: UserCheck, testid: "nav-backoffice-nieuwe-klanten", section: "backoffice" },
    { to: "/backoffice/events", label: "Events", icon: CalendarDays, testid: "nav-backoffice-events", section: "backoffice" },
    { to: "/backoffice/suggesties", label: "Suggesties", icon: Lightbulb, testid: "nav-suggestions", suggest: true, sections: ["backoffice", "sales"] },
    { to: "/backoffice/fathom", label: "Fathom-opnames", icon: Sparkles, testid: "nav-fathom", section: "backoffice" },
    { to: "/backoffice/achterstanden", label: "Achterstanden", icon: AlertTriangle, testid: "nav-backoffice-achterstanden", section: "backoffice" },
    { to: "/backoffice/abonnementen", label: "Abonnementen", icon: Repeat, testid: "nav-backoffice-abonnementen", section: "backoffice" },
    { to: "/backoffice/refunds", label: "Refunds", icon: Undo2, testid: "nav-backoffice-refunds", section: "backoffice" },
    { to: "/backoffice/ltv", label: "LTV-rapportage", icon: Coins, testid: "nav-backoffice-ltv", section: "backoffice" },
  ] },
  { label: "Rapportage", items: [
    { to: "/rapportage", label: "Rapportage", icon: BarChart3, testid: "nav-rapportage", end: true, section: "rapportage" },
    { to: "/rapportage/omzet", label: "Omzet", icon: Banknote, testid: "nav-rapportage-omzet", section: "rapportage" },
    { to: "/rapportage/webinar", label: "Webinar", icon: MonitorPlay, testid: "nav-rapportage-webinar", section: "rapportage" },
    { to: "/rapportage/uitstroom", label: "Uitstroom trajecten", icon: DoorOpen, testid: "nav-rapportage-uitstroom", section: "rapportage" },
    { to: "/tv", label: "TV-modus", icon: Tv, testid: "nav-tv", section: "rapportage" },
  ] },
  { label: "Team", items: [
    { to: "/team", label: "Teamdashboard", icon: Crown, testid: "nav-team", section: "team_dashboards" },
    { to: "/logboek", label: "Logboek", icon: ScrollText, testid: "nav-logboek", section: "logboek" },
  ] },
  { label: "Klanten V2", items: [
    { to: "/klanten-v2", label: "Klanten V2", icon: Kanban, testid: "nav-klanten-v2", section: "klanten" },
  ] },
];

const PATH_SECTIONS = [
  ["/dashboard/team-dag", "team_dashboards"], ["/dashboard/sales", "team_dashboards"],
  ["/dashboard/support", "team_dashboards"], ["/dashboard/csm", "team_dashboards"],
  ["/dashboard", "mijn_dag"], ["/tasks", "mijn_dag"],
  ["/sales/suggesties", ["sales", "backoffice"]], ["/backoffice/suggesties", ["sales", "backoffice"]],
  ["/sales", "sales"], ["/schatkaart", "sales"],
  ["/customers", "klanten"], ["/duplicaten", "klanten"], ["/klanten-v2", "klanten"],
  ["/support", "support"], ["/backoffice", "backoffice"],
  ["/rapportage", "rapportage"], ["/tv", "rapportage"],
  ["/team", "team_dashboards"], ["/admin", "admin"], ["/logboek", "logboek"],
];

const MOBILE_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, section: "mijn_dag" },
  { to: "/sales/funnel", label: "Sales", icon: TrendingUp, section: "sales" },
  { to: "/customers", label: "Klanten", icon: Users, section: "klanten" },
  { to: "/support/inbox", label: "Support", icon: LifeBuoy, section: "support" },
  { to: "/tasks", label: "Taken", icon: CheckSquare, section: "mijn_dag" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sup, setSup] = useState({});
  const [sugPending, setSugPending] = useState(0);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [compact, setCompact] = useState(() => localStorage.getItem("density") === "compact");

  const toggleSound = () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOn(next);
  };

  const toggleDensity = () => {
    setCompact((c) => {
      const next = !c;
      localStorage.setItem("density", next ? "compact" : "comfortable");
      return next;
    });
  };

  useEffect(() => {
    document.documentElement.classList.toggle("density-compact", compact);
  }, [compact]);

  useEffect(() => {
    let active = true;
    const load = () => api.get("/support/stats").then((r) => active && setSup(r.data || {})).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    const onStats = (e) => { if (e.detail) setSup(e.detail); else load(); };
    window.addEventListener("support:stats", onStats);
    return () => { active = false; clearInterval(id); window.removeEventListener("support:stats", onStats); };
  }, []);

  useEffect(() => {
    let active = true;
    const load = () => api.get("/suggestions/stats").then((r) => active && setSugPending(r.data?.pending || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    window.addEventListener("suggestions:refresh", load);
    return () => { active = false; clearInterval(id); window.removeEventListener("suggestions:refresh", load); };
  }, []);

  const supportCount = (key) => ({
    alle: sup.unread_mine, whatsapp: sup.whatsapp,
  }[key] || 0);

  const allowed = user?.sections ? new Set(user.sections) : null;
  const sections = (user?.role === "admin"
    ? SECTIONS.map((s) => (s.label === "Team"
        ? { ...s, items: [...s.items,
            { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin", section: "admin" },
            { to: "/admin/events", label: "Events-beheer", icon: CalendarDays, testid: "nav-admin-events", section: "admin" },
            { to: "/admin/workflows", label: "Workflows", icon: Workflow, testid: "nav-admin-workflows", section: "admin" }] }
        : s))
    : SECTIONS)
    .map((s) => (allowed ? { ...s, items: s.items.filter((it) =>
      (!it.section && !it.sections) || (it.section && allowed.has(it.section))
      || (it.sections || []).some((sec) => allowed.has(sec))) } : s))
    .filter((s) => s.items.length > 0);

  const firstAllowedPath = sections[0]?.items?.[0]?.to || "/dashboard";
  useEffect(() => {
    if (!allowed) return;
    const hit = PATH_SECTIONS.find(([p]) => location.pathname.startsWith(p));
    const need = hit ? (Array.isArray(hit[1]) ? hit[1] : [hit[1]]) : null;
    if (need && !need.some((s) => allowed.has(s))) navigate(firstAllowedPath, { replace: true });
  }, [location.pathname, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#0b1f4d] text-white fixed h-full">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
          <div className="h-9 w-9 rounded-lg bg-emerald-500 flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <span className="font-heading text-lg font-bold">Dividend CS</span>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-5 overflow-y-auto">
          {sections.map((sec) => (
            <div key={sec.label}>
              <p className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-blue-300/70 flex items-center gap-2">
                {sec.label}
                {sec.support && sup.nieuw > 0 && (
                  <span data-testid="nav-support-badge" title={`${sup.nieuw} nieuw ticket(s)`}
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold animate-in zoom-in">
                    {sup.nieuw}
                  </span>
                )}
              </p>
              <div className="space-y-0.5">
                {sec.items.map((item) => sec.support ? (() => {
                  const onSupport = location.pathname.startsWith("/support") && location.pathname !== "/support/whatsapp";
                  const curKanaal = new URLSearchParams(location.search).get("kanaal") || "";
                  const active = onSupport && curKanaal === (item.kanaal || "");
                  return (
                  <NavLink key={item.to} to={item.to} data-testid={item.testid}
                    className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                        active ? "bg-white/10 text-white font-semibold" : "text-blue-100 hover:bg-white/5 hover:text-white"
                      }`}>
                    <span className="flex items-center gap-2.5"><span className={`h-2 w-2 rounded-full ${STATUS_DOT[item.key]}`} />{item.label}</span>
                    {supportCount(item.key) > 0 && (
                      <span data-testid={`${item.testid}-count`}
                        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                          item.key === "alle" ? "bg-red-500/90 text-white" : "bg-white/10 text-blue-100"
                        }`}>
                        {supportCount(item.key)}
                      </span>
                    )}
                  </NavLink>
                  );
                })() : (
                  <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "bg-white/10 text-white" : "text-blue-100 hover:bg-white/5 hover:text-white"
                      }`
                    }>
                    <item.icon className="h-[18px] w-[18px]" />
                    <span>{item.label}</span>
                    {item.suggest && sugPending > 0 && (
                      <span data-testid="nav-suggestions-badge" title={`${sugPending} openstaande suggestie(s)`}
                        className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                        {sugPending}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 text-xs text-blue-300 border-t border-white/10">
          Intern customer-success platform
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0">
        <header className="sticky top-0 z-40 h-16 bg-white/90 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-6">
          <div className="md:hidden flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-700" />
            <span className="font-heading font-bold text-slate-900">Dividend CS</span>
          </div>
          <div className="flex-1 flex justify-center px-4">
            <GlobalSearch />
          </div>
          <SavedViews />
          <button data-testid="density-toggle-btn" onClick={toggleDensity}
            title={compact ? "Compacte weergave aan — klik voor normale weergave" : "Normale weergave — klik voor compacte tabellen"}
            className={`mr-1 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${
              compact ? "text-blue-700 hover:bg-blue-50" : "text-slate-400 hover:bg-slate-100"
            }`}>
            {compact ? <Rows4 className="h-5 w-5" /> : <Rows3 className="h-5 w-5" />}
          </button>
          <button data-testid="sound-toggle-btn" onClick={toggleSound}
            title={soundOn ? "Meldingsgeluid aan — klik om uit te zetten" : "Meldingsgeluid uit — klik om aan te zetten"}
            className={`mr-1 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${
              soundOn ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"
            }`}>
            {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="flex items-center gap-3 rounded-full hover:bg-slate-100 p-1 pr-3 transition-colors">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                    {initials(user?.name || user?.email || "U")}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{user?.name}</p>
                  <p className="text-xs text-slate-500 leading-tight">{user?.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="handleiding-menu-item" onClick={() => navigate("/handleiding")} className="cursor-pointer">
                <LifeBuoy className="h-4 w-4 mr-2" /> Handleiding
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="processen-menu-item" onClick={() => navigate("/processen")} className="cursor-pointer">
                <ScrollText className="h-4 w-4 mr-2" /> Processen
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="settings-menu-item" onClick={() => navigate("/settings")} className="cursor-pointer">
                <SettingsIcon className="h-4 w-4 mr-2" /> Instellingen
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-600 cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" /> Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-6 sm:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <GoogleTokenBanner />
          <TaskNotifications />
          <TaskCelebrationOverlay />
          <CommApprovalPopup />
          <Suspense fallback={<div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>}>
            <Outlet />
          </Suspense>
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden sticky bottom-0 bg-white border-t border-slate-200 flex justify-around py-2">
          {MOBILE_ITEMS.filter((item) => !allowed || !item.section || allowed.has(item.section)).map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-4 py-1 text-xs ${isActive ? "text-blue-700" : "text-slate-500"}`
            }>
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <NotificationCenter />
    </div>
  );
}
